import { GroupType } from '@/features/admin/groups/groupTypes'
import { UserType } from '@/features/admin/users/userTypes'
import { CustomerType } from '@/features/internal/customers/customerTypes'
import type { SupportEntityType } from '@/features/internal/support/support-tabs'
import { OrganizationType } from '@/features/organizations/organizationTypes'

import type React from 'react'

import type { PermissionPair } from '@wci/wv-db-schemas'

// A tab is either ungated (no entity/action) or gated by a valid permission
// pair. The union preserves per-entity action narrowing instead of falling
// back to bare strings.
export type TabItem = {
  value: string
  label: string
  /** Optional lucide-style icon component rendered before the label. */
  icon?: React.ComponentType<{ className?: string }>
  /** When true, render a destructive alert next to the label (e.g. for
   *  hub-sourced tabs when the upstream server is unreachable). */
  stale?: boolean
} & (PermissionPair | { entity?: undefined; action?: undefined })

export type ParentBreadcrumb = {
  label: string | ((params: unknown) => string)
  /** Optional. When omitted, the breadcrumb renders as a non-navigable label
   *  — useful for parent grouping segments like "Support" that don't have a
   *  landing route of their own. */
  href?:
    | string
    | ((params: unknown, orgSlug?: string, customerId?: string) => string)
}

export type SectionConfigEntry<T = unknown> = {
  title:
    | string
    | ((
        params: unknown,
        data?: T,
        searchParams?: Record<string, string>
      ) => string)
  description?: string
  breadcrumb?: string | ((params: unknown, data?: unknown) => string)
  parentBreadcrumb?: ParentBreadcrumb | ParentBreadcrumb[]
  showBreadcrumbs?: boolean
  showContext?: boolean
  contextLabel?: string
  contextValue?: string | ((params: unknown, data?: T) => string)
  tabs?: TabItem[]
  href?: string
  usesHubServer?: boolean
  supportsMultiServer?: boolean
  requiredFeature?:
    | 'hasDisplays'
    | 'hasCableModems'
    | 'hasPmsData'
    | 'hasRoomGrid'
  showServerSelector?: boolean // Controls whether to render HubServerSelector in header (defaults to true)
  /**
   * Render this route full-bleed: the section layout drops its page padding +
   * ScrollArea and hands the content the raw full-height region. For pages that
   * own their own scroll/height — self-scrolling DataTables (the filter-rail
   * redesign), split-pane file browsers, canvases (topology/map/room grid), and
   * sticky-footer stepper forms. Replaces the old route-regex list in
   * use-full-bleed.ts; resolved from the matched route at render (flash-free).
   */
  fullBleed?: boolean
  /**
   * Support-entity tab map key. When set, the breadcrumb builder swaps the
   * trailing "Details" segment for the active tab's label (driven by
   * `?tab=…`) using `SUPPORT_TABS[tabKey]`. Drives this off the route
   * config explicitly instead of grepping the title for entity keywords.
   */
  tabKey?: SupportEntityType
  /**
   * Opt this route out of presence ("who else is viewing this").
   *
   * Presence is ON by default and its rooms are DERIVED, not declared — the
   * route's scope-bearing params become the room chain (see
   * `src/lib/presence/scopes.ts`). A route with no scope param therefore gets
   * no presence without anyone saying so, which already covers every org
   * dashboard, list page, report and utility.
   *
   * So this flag is a rare escape hatch: a route that DOES carry a record id
   * but still shouldn't collect viewers. If you reach for it, say why.
   */
  presence?: false
}

export type SectionConfig = {
  [path: string]: SectionConfigEntry<
    OrganizationType | UserType | GroupType | CustomerType
  >
}

export function getMatchedSection<T extends Record<string, unknown>>(
  pathname: string,
  config: T,
  basePrefix?: string // e.g., 'admin' or 'portal'
): {
  matched: T[string] | undefined
  href: string | undefined
  params: Record<string, string>
  /** The config key that matched, e.g. `/internal/[id]/support/cases/[caseId]`.
   *  Presence derives its room chain from this key's dynamic segments — the
   *  order and the param names both matter, and only the key carries them. */
  route: string | undefined
} {
  const parts = pathname.split('/').filter(Boolean)

  let normalizedPathname = pathname

  if (basePrefix && parts.length >= 2 && parts[1] === basePrefix) {
    normalizedPathname = '/' + parts.slice(1).join('/')
  }

  let extractedParams: Record<string, string> = {}

  // Literal matches always win over dynamic patterns regardless of declaration
  // order — otherwise `/admin/roles/new` could be swallowed by `/admin/roles/[id]`
  // if the dynamic entry happens to be declared first.
  const literalEntry = Object.entries(config).find(
    ([key]) => key === normalizedPathname
  )
  const entry =
    literalEntry ??
    Object.entries(config).find(([key]) => {
      if (!key.includes('[')) return false

      const keySegments = key.split('/').filter(Boolean)
      const pathSegments = normalizedPathname.split('/').filter(Boolean)

      if (keySegments.length !== pathSegments.length) return false

      const params: Record<string, string> = {}
      const isMatch = keySegments.every((segment, i) => {
        if (segment.startsWith('[') && segment.endsWith(']')) {
          const paramName = segment.slice(1, -1)
          params[paramName] = pathSegments[i]
          return true
        }
        return segment === pathSegments[i]
      })

      if (isMatch) {
        extractedParams = params
        return true
      }
      return false
    })

  if (!entry) {
    return { matched: undefined, href: undefined, params: {}, route: undefined }
  }

  const [route, section] = entry

  return {
    matched: section as T[string],
    href: (section as { href?: string }).href ?? route.split('/[')[0],
    params: extractedParams,
    route
  }
}
